import { RouteGetCdtCategories_categoryId_Tree } from "@CommonApi/cdt/categories/_categoryId_/tree";
import type { ResponseGetCdtCategoryTree } from "@CommonApi/cdt/categories/_categoryId_/tree";
import { RouteGetCdtCategories_categoryId_Stores } from "@CommonApi/cdt/categories/_categoryId_/stores";
import type { ResponseGetCdtCategoryStores } from "@CommonApi/cdt/categories/_categoryId_/stores";
import { RouteGetCdtCategories_categoryId_Trees } from "@CommonApi/cdt/categories/_categoryId_/trees";
import type { ResponseGetCdtCategoryTrees } from "@CommonApi/cdt/categories/_categoryId_/trees";
import { RoutePutCdtCategories_categoryId_DefaultTree } from "@CommonApi/cdt/categories/_categoryId_/default-tree";
import type {
  PutCategoryDefaultTreeBody,
  ResponsePutCategoryDefaultTree,
} from "@CommonApi/cdt/categories/_categoryId_/default-tree";
import { RouteGetCdtTrees_treeId_ } from "@CommonApi/cdt/trees/_treeId_/index";
import type { ResponseGetCdtTree } from "@CommonApi/cdt/trees/_treeId_/index";
import { RoutePutCdtTrees_treeId_ } from "@CommonApi/cdt/trees/_treeId_/put";
import type { PutCdtTree } from "@CommonApi/cdt/trees/_treeId_/put";
import { RouteGetCdtTrees_treeId_Items } from "@CommonApi/cdt/trees/_treeId_/items";
import { QueryGetCdtTreeItemsSchema } from "@CommonApi/cdt/trees/_treeId_/items";
import type {
  QueryGetCdtTreeItems,
  QueryGetCdtTreeItemsInput,
  ResponseGetCdtTreeItems,
} from "@CommonApi/cdt/trees/_treeId_/items";
import { RoutePostCdtTrees_treeId_Reset } from "@CommonApi/cdt/trees/_treeId_/reset";
import type { ResponsePostCdtTrees_treeId_Reset } from "@CommonApi/cdt/trees/_treeId_/reset";
import { RoutePostCdtTrees_treeId_Clone } from "@CommonApi/cdt/trees/_treeId_/clone";
import type {
  PostCloneCdtTreeBody,
  ResponsePostCloneCdtTree,
} from "@CommonApi/cdt/trees/_treeId_/clone";
import { RouteCdtCategories_categoryId_Stores_storeId_Tree } from "@CommonApi/cdt/categories/_categoryId_/stores/_storeId_/tree";
import type {
  PutCdtCategoryStoreTreeBody,
  ResponsePutCdtCategoryStoreTree,
} from "@CommonApi/cdt/categories/_categoryId_/stores/_storeId_/tree";
import { RouteGetCategories_categoryId_ } from "@CommonApi/categories/_categoryId_";
import type { ResponseGetCategories_categoryId_ } from "@CommonApi/categories/_categoryId_";
import { isAxiosError } from "axios";
import { API, getRequest, putRequest, postRequest, deleteRequest } from "src/utils/axios";

/** The category's codes, needed to find it in the merchflow category lists. */
export const fetchCdtCategory = async (
  categoryId: number,
): Promise<ResponseGetCategories_categoryId_> => {
  const url = `${API}${RouteGetCategories_categoryId_.replace(":categoryId", categoryId.toString())}`;
  const { data } = await getRequest<ResponseGetCategories_categoryId_>(url);
  return data;
};

export const fetchCdtCategoryTree = async (
  categoryId: number,
): Promise<ResponseGetCdtCategoryTree> => {
  const url = `${API}${RouteGetCdtCategories_categoryId_Tree.replace(":categoryId", categoryId.toString())}`;
  const { data } = await getRequest<ResponseGetCdtCategoryTree>(url);
  return data;
};

export const fetchCdtCategoryTrees = async (
  categoryId: number,
): Promise<ResponseGetCdtCategoryTrees> => {
  const url = `${API}${RouteGetCdtCategories_categoryId_Trees.replace(":categoryId", categoryId.toString())}`;
  const { data } = await getRequest<ResponseGetCdtCategoryTrees>(url);
  return data;
};

export const fetchCdtCategoryStores = async (
  categoryId: number,
): Promise<ResponseGetCdtCategoryStores> => {
  const url = `${API}${RouteGetCdtCategories_categoryId_Stores.replace(":categoryId", categoryId.toString())}`;
  const { data } = await getRequest<ResponseGetCdtCategoryStores>(url);
  return data;
};

export const putCdtCategoryDefaultTree = async (
  categoryId: number,
  payload: PutCategoryDefaultTreeBody,
): Promise<ResponsePutCategoryDefaultTree> => {
  const url = `${API}${RoutePutCdtCategories_categoryId_DefaultTree.replace(":categoryId", categoryId.toString())}`;
  const { data } = await putRequest<ResponsePutCategoryDefaultTree>(url, { payload });
  return data;
};

export const fetchCdtTree = async (treeId: number): Promise<ResponseGetCdtTree> => {
  const url = `${API}${RouteGetCdtTrees_treeId_.replace(":treeId", treeId.toString())}`;
  const { data } = await getRequest<ResponseGetCdtTree>(url);
  return data;
};

export const putCdtTree = async (
  treeId: number,
  payload: PutCdtTree,
): Promise<ResponseGetCdtTree> => {
  const url = `${API}${RoutePutCdtTrees_treeId_.replace(":treeId", treeId.toString())}`;
  const { data } = await putRequest<ResponseGetCdtTree>(url, { payload });
  return data;
};

export const normalizeCdtTreeItemsQuery = (
  input: QueryGetCdtTreeItemsInput,
): QueryGetCdtTreeItems => {
  const normalized = QueryGetCdtTreeItemsSchema.parse(input);

  return {
    ...(normalized.nodeId !== undefined ? { nodeId: normalized.nodeId } : {}),
    ...(normalized.unassigned !== undefined ? { unassigned: normalized.unassigned } : {}),
    ...(normalized.search !== undefined ? { search: normalized.search } : {}),
    page: normalized.page,
    size: normalized.size,
  };
};

export const fetchCdtTreeItems = async (
  treeId: number,
  input: QueryGetCdtTreeItemsInput,
): Promise<ResponseGetCdtTreeItems> => {
  const queryParams = normalizeCdtTreeItemsQuery(input);
  const url = `${API}${RouteGetCdtTrees_treeId_Items.replace(":treeId", treeId.toString())}`;
  const { data } = await getRequest<ResponseGetCdtTreeItems>(url, { queryParams });
  return data;
};

export const postResetCdtTree = async (
  treeId: number,
): Promise<ResponsePostCdtTrees_treeId_Reset> => {
  const url = `${API}${RoutePostCdtTrees_treeId_Reset.replace(":treeId", treeId.toString())}`;
  const { data } = await postRequest<ResponsePostCdtTrees_treeId_Reset>(url);
  return data;
};

/**
 * Copies a version (nodes, priorities and item assignments) into a new editable,
 * non-primary version of the same category with no pinned stores. Cloning a
 * read-only ETL snapshot is allowed; a duplicate editable name answers 409.
 */
export const postCloneCdtTree = async (
  treeId: number,
  payload: PostCloneCdtTreeBody,
): Promise<ResponsePostCloneCdtTree> => {
  const url = `${API}${RoutePostCdtTrees_treeId_Clone.replace(":treeId", treeId.toString())}`;
  const { data } = await postRequest<ResponsePostCloneCdtTree>(url, { payload });
  return data;
};

/** The clone endpoint's answer to a name another editable version of the category already uses. */
export const isCdtTreeNameConflict = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409;

/**
 * A CDT Studio tree finalized on a merchflow of the category: read-only in the
 * versions list, never primary, never pinnable. Contract pending in omni-common
 * (DEV-3612); the route answers 404 until the backend ships it.
 */
export const RouteGetCdtCategories_categoryId_FlowReleases =
  "/cdt/categories/:categoryId/flow-releases";

export interface CdtFlowRelease {
  treeId: number;
  treeName: string;
  merchflowId: number;
  merchflowName: string;
  basePogId: number | null;
  releaseId: number;
  finalizedAt: string;
}

export const fetchCdtCategoryFlowReleases = async (
  categoryId: number,
): Promise<CdtFlowRelease[]> => {
  const url = `${API}${RouteGetCdtCategories_categoryId_FlowReleases.replace(
    ":categoryId",
    categoryId.toString(),
  )}`;
  try {
    const { data } = await getRequest<CdtFlowRelease[]>(url);
    return data;
  } catch (error) {
    // No route yet (or no releases): the group simply does not show.
    if (isAxiosError(error) && error.response?.status === 404) return [];
    throw error;
  }
};

const storeTreeUrl = (categoryId: number, storeId: number) =>
  `${API}${RouteCdtCategories_categoryId_Stores_storeId_Tree.replace(
    ":categoryId",
    categoryId.toString(),
  ).replace(":storeId", storeId.toString())}`;

/** Pins a store to a version of the category (replacing any earlier pin). */
export const putCdtCategoryStoreTree = async (
  categoryId: number,
  storeId: number,
  payload: PutCdtCategoryStoreTreeBody,
): Promise<ResponsePutCdtCategoryStoreTree> => {
  const { data } = await putRequest<ResponsePutCdtCategoryStoreTree>(
    storeTreeUrl(categoryId, storeId),
    { payload },
  );
  return data;
};

/** Removes a store's pin; the store returns to the category's primary CDT. */
export const deleteCdtCategoryStoreTree = async (
  categoryId: number,
  storeId: number,
): Promise<void> => {
  await deleteRequest<unknown>(storeTreeUrl(categoryId, storeId));
};
